import { TJobForm } from "@/features/job/types/JobForm";
import { background, divider, state, text } from "@/theme/colors";
import { containsNonNumeric } from "@/utils/input";
import { toNumber } from "@/utils/number";
import { convertFullWidthToHalfWidth } from "@/utils/pieces";
import { InputBase, Stack, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { Path, useFormContext } from "react-hook-form";

type THourMinuteAmountInputProps = {
  readonly: boolean;
  isError: boolean;
  nameHour: Path<TJobForm>;
  nameMinute: Path<TJobForm>;
  isClockFormat?: boolean;
  onChange?: (hour: string, min: string) => void;
};

export const HourMinuteAmountInput: FC<THourMinuteAmountInputProps> = ({
  onChange,
  readonly,
  isError,
  nameHour,
  nameMinute,
  isClockFormat = true,
}) => {
  const { getValues, setValue, trigger } = useFormContext<TJobForm>();

  const formHour = getValues(nameHour) ?? "";
  const formMinute = getValues(nameMinute) ?? "";

  const [hour, setHour] = useState<string>(formHour.toString());
  const [minute, setMinute] = useState<string>(formMinute.toString());

  const [isComposingHour, setIsComposingHour] = useState(false);
  const [isComposingMinute, setIsComposingMinute] = useState(false);

  useEffect(() => {
    setHour(formHour.toString());
  }, [formHour]);

  useEffect(() => {
    setMinute(formMinute.toString());
  }, [formMinute]);

  const restOnChange = (hour: string, minute: string) => {
    if (onChange) {
      onChange(hour, minute);
    }
  };

  const updateFormHour = (val: string) => {
    if (val === "") {
      setValue(nameHour, null, { shouldValidate: true });
      trigger(nameHour);
      return;
    }
    if (!containsNonNumeric(val)) {
      const num = toNumber(val);
      setValue(nameHour, num, { shouldValidate: true });
      trigger(nameHour);
    }
  };

  const updateFormMinute = (val: string) => {
    if (val === "") {
      setValue(nameMinute, null, { shouldValidate: true });
      trigger(nameMinute);
      return;
    }
    if (!containsNonNumeric(val)) {
      const num = toNumber(val);
      setValue(nameMinute, num, { shouldValidate: true });
      trigger(nameMinute);
    }
  };

  const onChangeHour: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const val = convertFullWidthToHalfWidth(e.target.value);
    if (containsNonNumeric(val)) return;

    setHour(val);

    if (!isComposingHour) {
      updateFormHour(val);
      restOnChange(val, minute);
    }
  };

  const onCompositionStartHour = () => {
    setIsComposingHour(true);
  };

  const onCompositionEndHour = (e: React.CompositionEvent<HTMLInputElement>) => {
    setIsComposingHour(false);
    const val = convertFullWidthToHalfWidth(e.currentTarget.value);
    updateFormHour(val);
    restOnChange(val, minute);
  };

  const onBlurHour = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsComposingHour(false);
    const val = convertFullWidthToHalfWidth(e.target.value);
    updateFormHour(val);
    restOnChange(val, minute);
  };

  const onKeyUpHour = () => {
    setIsComposingHour(false);
  };

  const onChangeMinute: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const val = convertFullWidthToHalfWidth(e.target.value);
    if (containsNonNumeric(val)) return;

    setMinute(val);

    if (!isComposingMinute) {
      updateFormMinute(val);
      restOnChange(hour, val);
    }
  };

  const onCompositionStartMinute = () => setIsComposingMinute(true);

  const onCompositionEndMinute = (e: React.CompositionEvent<HTMLInputElement>) => {
    setIsComposingMinute(false);
    const val = convertFullWidthToHalfWidth(e.currentTarget.value);
    updateFormMinute(val);
    restOnChange(hour, val);
  };

  const onBlurMinute = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsComposingMinute(false);
    const val = convertFullWidthToHalfWidth(e.target.value);
    updateFormMinute(val);
    restOnChange(hour, val);
  };

  const onKeyUpMinute = () => {
    setIsComposingMinute(false);
  };

  return (
    <Stack
      direction="row"
      gap={0.4}
      justifyContent="space-between"
      alignItems="center"
      border={`1px solid ${isError ? state.error_1 : divider.middle}`}
      bgcolor={readonly ? background.disable : isError ? "#FCDFE4" : undefined}
      borderRadius={1}
      sx={{ pointerEvents: readonly ? "none" : undefined }}
      px={1}
    >
      <InputBase
        sx={{ maxWidth: "40px", fontSize: "14px" }}
        value={hour}
        onChange={onChangeHour}
        onCompositionStart={onCompositionStartHour}
        onCompositionEnd={onCompositionEndHour}
        onBlur={onBlurHour}
        onKeyUp={onKeyUpHour}
        placeholder="0"
        readOnly={readonly}
      />
      <Typography variant="body14" color={text.primary}>
        {isClockFormat ? "時" : "時間"}
      </Typography>
      <InputBase
        sx={{ maxWidth: "40px", fontSize: "14px" }}
        value={minute}
        onChange={onChangeMinute}
        onCompositionStart={onCompositionStartMinute}
        onCompositionEnd={onCompositionEndMinute}
        onBlur={onBlurMinute}
        onKeyUp={onKeyUpMinute}
        placeholder="00"
        readOnly={readonly}
      />
      <Typography variant="body14" color={text.primary}>
        分
      </Typography>
    </Stack>
  );
};
